import React, { useState } from 'react';
import { Card } from '../ui/card';
import { useLanguage } from '../../context/LanguageContext';
import { LongitudinalHistory } from './LongitudinalTables';
import './longitudinal.css';

export const LongitudinalProgress = ({ label, block, team = false, testId }) => {
  const { language } = useLanguage();
  const t = (en, it) => language === 'IT' ? it : en;
  const [selected, setSelected] = useState('');
  const series = block?.series || [];
  const preferredMetrics = ['jump_height_cm', 'pmax', 'mas_vam_kmh'];
  const initial = block?.report_series?.find(s => preferredMetrics.includes(s.metric)) || block?.report_series?.[0];
  const current = series.find(s => s.series_id === selected) || initial || series[0];
  const insufficient = t('Insufficient longitudinal data', 'Dati longitudinali insufficienti');
  const fmt = value => value == null ? '—' : Number(value).toLocaleString(language === 'IT' ? 'it-IT' : 'en-GB', { maximumFractionDigits: 2 });
  const values = [
    ['first', team ? t('Mean paired first', 'Media iniziale appaiata') : t('First', 'Primo')],
    ['best', t('Best observation', 'Migliore osservazione')],
    ['latest', team ? t('Mean paired latest', 'Media finale appaiata') : t('Latest', 'Ultimo')],
  ];
  const coverage = current?.coverage || { comparable_athletes: 0, relevant_athletes: 0 };
  return (
    <Card className="bg-zinc-900 border-zinc-800 p-4 min-w-0" data-testid={testId}>
      <div className="text-xs uppercase text-zinc-400 mb-3" data-testid={`${testId}-title`}>{label}</div>
      {series.length > 0 && <>
        <select aria-label={t('Comparable series', 'Serie confrontabile')} data-testid={`${testId}-series-select`}
          value={current.series_id} onChange={e => setSelected(e.target.value)}
          className="w-full min-w-0 max-w-full bg-zinc-950 text-zinc-200 border border-zinc-700 rounded p-2 text-xs mb-3 focus:border-blue-400 transition-colors">
          {series.map(s => <option key={s.series_id} value={s.series_id} data-testid={`${testId}-option-${s.series_id}`}>{s.label}</option>)}
        </select>
        <p className="text-xs text-zinc-400 break-words mb-3" data-testid={`${testId}-series-label`}>{current.label}</p>
        <div className="grid grid-cols-3 gap-2 text-center">
          {values.map(([key, title]) => <div key={key} className="min-w-0">
            <div className="text-[10px] text-zinc-400 break-words" data-testid={`${testId}-${key}-label`}>{title}</div>
            <div className="text-lg font-bold text-zinc-100" data-testid={`${testId}-${key}`}>{fmt(current[key])}</div>
          </div>)}
        </div>
        {!current.direction && <p className="text-xs text-zinc-400 mt-2" data-testid={`${testId}-unknown-direction`}>
          {t('Best: direction not defined', 'Migliore: direzione non definita')}
        </p>}
      </>}
      <div className="mt-4 text-center text-sm text-zinc-200" data-testid={`${testId}-change`}>
        <div className="text-xs text-zinc-400 mb-1" data-testid={`${testId}-change-label`}>
          {team ? t('Mean individual change', 'Variazione individuale media') : t('Individual change', 'Variazione individuale')}
        </div>
        <span data-testid={`${testId}-delta`}>{current?.delta_pct == null ? insufficient : `${current.delta_pct >= 0 ? '+' : ''}${fmt(current.delta_pct)}%`}</span>
      </div>
      {team && <p className="mt-3 text-xs text-zinc-400 text-center" data-testid={`${testId}-coverage`}>
        {coverage.comparable_athletes}/{coverage.relevant_athletes} {t('athletes with comparable longitudinal data', 'atleti con dati longitudinali confrontabili')}
      </p>}
      {current && <LongitudinalHistory series={current} team={team} testId={testId} t={t} />}
    </Card>
  );
};